const { PGAdapter } = require('../common')
const { locationTransactionListQuery } = require('./queries/wallet-v2/transaction-list-query')
const payableListQuery = require('./queries/wallet-v2/payable-list-query')
const clientBalanceQuery = require('./queries/wallet-v2/client-balance-query')
const { pendingPaymentBalanceQuery } = require('./queries/wallet-v2/pending-payments-query')
const { SHIPMENT_WINDOW } = require('./constants')
const { groupedInstalments } = require('./tools/index')

class TransactionsAdapterV2 extends PGAdapter {
  constructor (pgConnection, username, dataShipment, creditApplication, locationApi) {
    super(pgConnection, username)
    this.dataShipment = dataShipment
    this.creditApplication = creditApplication
    this.locationApi = locationApi
  }

  async getList ({
    limit = 50,
    offset = 0,
    locationIds,
    txnType,
    noLimit, // in case for pdf statement we need to fetch all at once
    overduePayment = false,
    startDate,
    endDate
  }) {
    const { query, values } = locationTransactionListQuery({
      locationIds,
      limit,
      offset,
      txnType,
      noLimit,
      overduePayment,
      startDate,
      endDate
    })

    const { rows } = await this.pgConnection.query(query, values)
    return { results: rows }
  }

  async getPayablesList ({locationId}) {
    const { rows } = await this.pgConnection.query(payableListQuery, [locationId])
    return { results: rows }
  }

  async getCreditInfo ({ locationUuid }) {
    const { rows: [balance] } = await this.pgConnection.query(clientBalanceQuery, [locationUuid])

    if (!balance) {
      const error = new Error('Location not found')
      error.status = 404
      throw error
    }

    const [
      { status: creditApplicationStatus },
      pendingDeliveries,
      { rows: [pendingPaymentBalance] }
    ] = await Promise.all([
      this.creditApplication.get(locationUuid),
      this.dataShipment.getPendingDeliveries({
        locationId: balance.fsid,
        weeks: SHIPMENT_WINDOW
      }),
      this.pgConnection.query(pendingPaymentBalanceQuery, [locationUuid])
    ])

    const { pendingDeliveriesAmount, loanDeliveryAmountUsed } = pendingDeliveries
      .reduce((acc, pendingShipment) => {
        if (!pendingShipment.deliveryInvoiceAmount) {
          acc.loanDeliveryAmountUsed += pendingShipment.deliveryAmount
        }
        acc.pendingDeliveriesAmount += pendingShipment.deliveryAmount
        return acc
      }, { pendingDeliveriesAmount: 0, loanDeliveryAmountUsed: 0 })

    return {
      creditLimit: balance.loan_limit,
      creditCustomer: !!(balance.loan_limit && balance.loan_limit),
      orderStatus: balance.orders_status,
      availableCreditBalance: (balance.loan_limit || 0) - loanDeliveryAmountUsed - balance.overall_balance,
      pendingDeliveriesAmount,
      cashBalance: balance.overdue_balance <= 0 ? Math.abs(balance.overdue_balance) : 0,
      creditApplicationStatus,
      pendingPaymentsAmount: pendingPaymentBalance.amount,
      // -ve balance figures indicate the clients has a positive wallet balance, and hence isn't owing
      balances: {
        balance: balance.overall_balance * -1,
        pending: (balance.overall_balance - pendingPaymentBalance.amount) * -1,
        overdue: Math.max(balance.overdue_balance, 0)
      }
    }
  }

  async getAccountStatementSummary ({ locationUuid, startDate, endDate }) {
    const [
      { results: transactionList },
      creditInfo,
      location
    ] = await Promise.all([
      this.getList({
        locationIds: [locationUuid],
        noLimit: true,
        startDate,
        endDate,
        txnType: null
      }),
      this.getCreditInfo({ locationUuid }),
      this.locationApi.pgAdapter.getOneWithMarketData(locationUuid)
    ])

    const currentDate = new Date().toJSON()
    const payableTxnTypes = ['invoice', 'installment']
    const duePayables = transactionList
      .filter(txn => {
        return txn.amount_paid < txn.amount && payableTxnTypes.includes(txn.txn_type) && currentDate > new Date(txn.due_date).toJSON()
      })
    const paymentPlans = groupedInstalments(transactionList.filter(t => t.txn_type === 'installment'))

    return {
      duePayables,
      transactionList,
      balance: creditInfo.balances,
      location,
      paymentPlans
    }
  }
}

module.exports = TransactionsAdapterV2
